/**
 * DeviceList - Display user's devices with verification status
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { CryptoEvent } from 'matrix-js-sdk/lib/crypto-api';
import { useMatrixClient } from '../../context/MatrixContext';
import { useModal } from '../../context/ModalContext';
import VerifiedBadge from './VerifiedBadge';
import VerificationModal from '../verification/VerificationModal';
import PasswordInputModal from '../Modal/PasswordInputModal';
import { useDeviceVerificationStatus } from '../../hooks/useDeviceVerificationStatus';
import matrixService from '../../services/matrixService';
import logger from '../../utils/logger';
import './DeviceList.css';

// Time threshold (in milliseconds) for considering timestamps "significantly different"
// Prevents visual reordering from minor timestamp changes during sync
const SIGNIFICANT_TIME_DIFF_MS = 5 * 60 * 1000; // 5 minutes

/**
 * DeviceListItem Component
 */
const DeviceListItem = ({ device, isCurrentDevice, onVerify, onLogout }) => {
  const { isVerified, isCrossSigned } = useDeviceVerificationStatus(
    device.userId,
    device.deviceId,
  );

  return (
    <div className={`device-list-item ${isCurrentDevice ? 'device-list-item--current' : ''}`}>
      <div className="device-list-item__info">
        <div className="device-list-item__header">
          <h4 className="device-list-item__name">
            {device.displayName || device.deviceId}
            {isCurrentDevice && <span className="device-list-item__current-badge">(This device)</span>}
          </h4>
          <VerifiedBadge
            isVerified={isVerified}
            isCrossSigned={isCrossSigned}
            size="small"
          />
        </div>
        <div className="device-list-item__details">
          <span className="device-list-item__id">ID: {device.deviceId}</span>
          {device.lastSeenTs && (
            <span className="device-list-item__last-seen">
              Last seen: {new Date(device.lastSeenTs).toLocaleString()}
            </span>
          )}
        </div>
      </div>
      <div className="device-list-item__actions">
        {!isVerified && isCurrentDevice && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => onVerify(device)}
          >
            Verify now
          </button>
        )}
        {!isCurrentDevice && (
          <button
            className="device-logout-button"
            onClick={() => onLogout(device)}
            title="Sign out device"
            aria-label="Sign out device"
          >
            <ion-icon name="log-out-outline" />
          </button>
        )}
      </div>
    </div>
  );
};

DeviceListItem.propTypes = {
  device: PropTypes.shape({
    userId: PropTypes.string,
    deviceId: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    lastSeenTs: PropTypes.number,
  }).isRequired,
  isCurrentDevice: PropTypes.bool,
  onVerify: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};

/**
 * DeviceList Component
 * 
 * Displays a list of user's Matrix devices with their verification status.
 * Updates automatically when devices are added/removed via CryptoEvent.DevicesUpdated.
 * 
 * @param {Object} props
 * @param {string} [props.userId] - User ID to fetch devices for (defaults to current user)
 */
const DeviceList = ({ userId }) => {
  const client = useMatrixClient();
  const { showConfirm, showError: showErrorModal, showInfo } = useModal();
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [passwordModalState, setPasswordModalState] = useState({
    isOpen: false,
    deviceId: null,
    deviceName: null,
  });

  // Ref to avoid stale closure in password handler
  const passwordModalStateRef = useRef(passwordModalState);
  useEffect(() => {
    passwordModalStateRef.current = passwordModalState;
  }, [passwordModalState]);

  const currentDeviceId = client?.getDeviceId();
  const currentUserId = userId || client?.getUserId();

  // Fetch devices
  const fetchDevices = useCallback(async () => {
    if (!client || !currentUserId) {
      setError('Client not initialized');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get devices for user
      // Always use REST API to get ALL devices from server (matches Element Web behavior)
      // crypto.getUserDeviceInfo() only returns devices with crypto keys, missing some devices
      let deviceList = [];

      try {
        // Primary method: Use REST API to get all devices
        const response = await client.getDevices();
        deviceList = response.devices.map(device => ({
          userId: currentUserId,
          deviceId: device.device_id,
          displayName: device.display_name || device.device_id,
          lastSeenTs: device.last_seen_ts,
        }));
      } catch (err) {
        // Fallback: Try crypto API if REST API fails
        logger.warn('DeviceList: REST API failed, trying crypto API:', err);
        const crypto = client.getCrypto();
        if (crypto?.getUserDeviceInfo) {
          const devicesMap = await crypto.getUserDeviceInfo([currentUserId]);
          const userDevices = devicesMap.get(currentUserId);

          if (userDevices) {
            deviceList = Array.from(userDevices.values()).map(device => ({
              userId: currentUserId,
              deviceId: device.deviceId,
              displayName: device.displayName || device.deviceId,
              lastSeenTs: device.lastSeenTs,
            }));
          }
        }
      }

      /**
       * Sort devices with stable ordering to prevent visual jumping:
       * 1. Current device always appears first
       * 2. Other devices sorted by last seen time (most recent first)
       * 3. Only reorder if timestamp difference is significant (> 5 minutes)
       * 4. Fall back to alphabetical sort by deviceId for stability
       */
      deviceList.sort((a, b) => {
        // Current device always first
        if (a.deviceId === currentDeviceId) return -1;
        if (b.deviceId === currentDeviceId) return 1;
        
        // Sort by last seen timestamp, but only if difference is significant
        const timeDiff = (b.lastSeenTs || 0) - (a.lastSeenTs || 0);
        
        // Only reorder if timestamps differ by more than the threshold
        // This prevents visual reordering from minor timestamp updates during sync
        if (Math.abs(timeDiff) > SIGNIFICANT_TIME_DIFF_MS) {
          return timeDiff;
        }
        
        // For similar timestamps, use stable deviceId sort
        return a.deviceId.localeCompare(b.deviceId);
      });

      setDevices(deviceList);
      logger.debug(`DeviceList: Loaded ${deviceList.length} devices`);

    } catch (err) {
      logger.error('DeviceList: Failed to fetch devices:', err);
      setError('Failed to load devices');
    } finally {
      setIsLoading(false);
    }
  }, [client, currentUserId, currentDeviceId]);

  // Initial fetch
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Note: Removed sync event listener to prevent periodic updates.
  // Device list now only updates on:
  // 1. Initial load
  // 2. Manual refresh button click
  // 3. After verification completes
  // 4. When devices are added/removed (via CryptoEvent.DevicesUpdated)
  // Device verification status still updates in real-time via trust events.

  // Listen for device changes (when devices are added/removed)
  useEffect(() => {
    if (!client) return;

    const handleDevicesUpdated = (users, initialFetch) => {
      // Only refresh if it's the current user's devices and not initial fetch
      // This captures when devices are added/removed from other clients
      if (!initialFetch && users && users.includes(currentUserId)) {
        logger.debug('DeviceList: Devices updated for current user');
        fetchDevices();
      }
    };

    client.on(CryptoEvent.DevicesUpdated, handleDevicesUpdated);

    return () => {
      client.off(CryptoEvent.DevicesUpdated, handleDevicesUpdated);
    };
  }, [client, currentUserId, fetchDevices]);

  // Handle verify action
  const handleVerify = useCallback(() => {
    logger.info('User initiated device verification');
    setShowVerificationModal(true);
  }, []);

  // Handle verification complete
  const handleVerificationComplete = useCallback((success) => {
    logger.debug('DeviceList: Verification complete:', success);
    setShowVerificationModal(false);

    if (success) {
      // Refresh device list to show updated status
      fetchDevices();
    }
  }, [fetchDevices]);

  // Handle device logout
  const handleDeviceLogout = useCallback((device) => {
    const deviceName = device.displayName || device.deviceId;
    
    // Show confirmation dialog
    showConfirm(
      'Sign Out Device',
      `Are you sure you want to sign out "${deviceName}"?`,
      async () => {
        try {
          // First try without password (uses HTTP cookie if valid)
          const result = await matrixService.deleteDevice(device.deviceId);
          
          // If password needed, show modal
          if (!result.success && result.needsPassword) {
            logger.debug('Password required, showing password modal');
            setPasswordModalState({
              isOpen: true,
              deviceId: device.deviceId,
              deviceName: deviceName,
            });
            return;  // Wait for modal callback
          }
          
          // Handle success (HTTP cookie was valid)
          if (result.success) {
            showInfo('Success', `"${deviceName}" has been signed out successfully.`);
            fetchDevices();
          } else {
            showErrorModal('Sign Out Failed', result.message);
          }
        } catch (err) {
          logger.error('Error in device logout:', err);
          showErrorModal('Error', 'An unexpected error occurred.');
        }
      },
    );
  }, [showConfirm, showErrorModal, showInfo, fetchDevices]);

  // Handle password submission from modal
  const handlePasswordSubmit = useCallback(async (password) => {
    // Get current values from ref to avoid stale closure
    const { deviceId, deviceName } = passwordModalStateRef.current;
    
    // Close modal immediately before async operation
    setPasswordModalState({ isOpen: false, deviceId: null, deviceName: null });
    
    try {
      // Retry with password
      const result = await matrixService.deleteDevice(deviceId, password);
      
      if (result.success) {
        showInfo('Success', `"${deviceName}" has been signed out successfully.`);
        fetchDevices();
      } else {
        showErrorModal('Sign Out Failed', result.message);
      }
    } catch (err) {
      logger.error('Error in device logout with password:', err);
      showErrorModal('Error', 'An unexpected error occurred.');
    }
  }, [showInfo, showErrorModal, fetchDevices]);

  const handlePasswordCancel = useCallback(() => {
    logger.debug('User cancelled password input');
    setPasswordModalState({ isOpen: false, deviceId: null, deviceName: null });
  }, []);

  // Display all devices
  const displayedDevices = devices;

  if (isLoading) {
    return (
      <div className="device-list device-list--loading">
        <div className="spinner" />
        <p>Loading devices...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="device-list device-list--error">
        <p className="error-message">{error}</p>
        <button className="btn btn-primary" onClick={fetchDevices}>
          Try again
        </button>
      </div>
    );
  }

  if (displayedDevices.length === 0) {
    return (
      <div className="device-list device-list--empty">
        <p>No devices found</p>
      </div>
    );
  }

  return (
    <>
      <div className="device-list">
        <div className="device-list__header">
          <h3>Your Devices</h3>
          <button
            className="btn btn-secondary btn-sm"
            onClick={fetchDevices}
          >
            Refresh
          </button>
        </div>
        <div className="device-list__items">
          {displayedDevices.map(device => (
            <DeviceListItem
              key={device.deviceId}
              device={device}
              isCurrentDevice={device.deviceId === currentDeviceId}
              onVerify={handleVerify}
              onLogout={handleDeviceLogout}
            />
          ))}
        </div>
      </div>

      {showVerificationModal && (
        <VerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onComplete={handleVerificationComplete}
        />
      )}

      {/* Password input modal */}
      {/* CRITICAL: Use deviceId as key to force new component instance per device */}
      {/* This ensures password field is cleared when modal opens for a different device */}
      <PasswordInputModal
        key={passwordModalState.deviceId || 'closed'}
        isOpen={passwordModalState.isOpen}
        title="Enter Password"
        message={`Enter your password to sign out "${passwordModalState.deviceName || 'this device'}"`}
        confirmText="Sign Out"
        cancelText="Cancel"
        onConfirm={handlePasswordSubmit}
        onCancel={handlePasswordCancel}
      />
    </>
  );
};

DeviceList.propTypes = {
  userId: PropTypes.string,
};

export default DeviceList;